import { type TeamId, type TeamData, type TeamLevel, isNotDefined } from '@terros/common'

export type WithParentId = Pick<TeamData, 'teamId' | 'parentId'>

export type SortableTeam = WithParentId & Pick<TeamData, 'name'>

export const toDirectChildMap = <T extends WithParentId>(teams: T[]): Map<TeamId, T[]> => {
  const map = new Map<TeamId, T[]>()
  for (const team of teams) {
    const parent = team.parentId
    if (!parent) continue
    const children = map.get(parent) ?? []
    children.push(team)
    map.set(parent, children)
  }
  return map
}

export const getTeamScore = (childCount: number, parentPosition: number): number => {
  return parentPosition * 1000 + childCount * 100
}

export const getParentPositionsMap = (teams: WithParentId[]): Map<TeamId, number> => {
  const parentPositions = new Map<TeamId, number>()
  for (const [index, team] of teams.entries()) {
    parentPositions.set(team.teamId, index)
  }
  return parentPositions
}

export const sortTeams = <T extends SortableTeam>(
  teams: T[],
  sortedParentTeams: T[],
  directChildMap: Map<TeamId, T[]>
): T[] => {
  const parentPositions = getParentPositionsMap(sortedParentTeams)

  const sortedTeamsWithParents = [...teams].sort((a, b) => {
    const scoreA = getTeamScore(directChildMap.get(a.teamId)?.length ?? 0, getParentPosition(a, parentPositions))
    const scoreB = getTeamScore(directChildMap.get(b.teamId)?.length ?? 0, getParentPosition(b, parentPositions))

    if (scoreA === scoreB) {
      return a.name.localeCompare(b.name)
    }
    return scoreA - scoreB
  })

  return sortedTeamsWithParents
}

export const getLevelVisibleTeams = (teams: TeamData[], level: TeamLevel, collapsedTeams: Set<TeamId>): TeamData[] => {
  const visibleTeams = teams.filter((team) => team.level === level)
  return visibleTeams.filter((team) => {
    if (team.parentTeamIds.some((id) => collapsedTeams.has(id))) return false
    return true
  })
}

export const getParentPosition = (team: WithParentId, parentPositions: Map<TeamId, number>): number => {
  if (isNotDefined(team.parentId)) return 0
  return parentPositions.get(team.parentId) ?? 0
}

export const decorateTeamWithChildCount = <T extends SortableTeam>(teams: T[]): T[] => {
  const directChildMap = toDirectChildMap(teams)
  return teams.map((team) => {
    const children = directChildMap.get(team.teamId) ?? []
    return { ...team, childCount: children.length }
  })
}
